import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { rendicionAnualService, documentoService } from '../../../services/api';
import type { RendicionAnual } from './types';
import BotonRefresh from './BotonRefresh';

interface Version {
  id: string;
  numeroVersion: number;
  hashCode: string;
  fechaGeneracion: string;
  pdfGeneradoUrl: string | null;
  esBorrador?: boolean;
  usuario?: { nombre: string; apellido: string; email: string };
}

interface ValidacionItem {
  codigo: string;
  titulo: string;
  ok: boolean;
  detalle?: string;
}

interface ResultadoValidaciones {
  ok: boolean;
  cantidadOk: number;
  cantidadTotal: number;
  validaciones: ValidacionItem[];
  hayCambiosDesdeUltimaVersion: boolean;
  versionActual: number;
}

interface Props {
  rendicion: RendicionAnual;
  partidoId: string;
  onBack: () => void;
  onPresentada: (info: { numeroExpediente: string; expedienteId: string; esRepresentacion?: boolean }) => void;
}

function formatFecha(f: string): string {
  return new Date(f).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function Paso6FirmaEnvio({ rendicion, partidoId, onBack, onPresentada }: Props) {
  const queryClient = useQueryClient();
  const [docFirmadoNombre, setDocFirmadoNombre] = useState<string | null>(null);
  const [ddjjAceptada, setDdjjAceptada] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [descargandoBorrador, setDescargandoBorrador] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const rendicionId = rendicion.id;
  const readonly = rendicion.estado !== 'BORRADOR' && rendicion.estado !== 'OBSERVADA';
  const esRepresentacion = rendicion.estado === 'OBSERVADA';

  const { data: versionesData } = useQuery({
    queryKey: ['rendicion-versiones', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.listVersiones(rendicionId);
      return (res.data ?? []) as Version[];
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const versiones = versionesData ?? [];

  // Versión actual = la primera del listado (orderBy desc por numeroVersion)
  const versionActualRecord = versiones.find((v) => v.numeroVersion === rendicion.versionActual) ?? null;
  const tieneVersion = !!versionActualRecord;
  const versionEsBorrador = !!versionActualRecord?.esBorrador;
  const versionEsFirmada = tieneVersion && !versionEsBorrador;

  const { data: validacionesData } = useQuery({
    queryKey: ['rendicion-validaciones', rendicionId],
    queryFn: async () => {
      const res = await rendicionAnualService.getValidaciones(rendicionId);
      return res.data as ResultadoValidaciones;
    },
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const validacionesOk = validacionesData?.ok ?? false;
  // El backend compara el hash de los datos actuales con el hash de la versión
  // actual. Si difieren, el PDF de la versión está desactualizado y la única
  // acción permitida es regenerar el borrador.
  const hayCambiosDesdeUltimaVersion = validacionesData?.hayCambiosDesdeUltimaVersion ?? false;
  const versionDesactualizada = tieneVersion && hayCambiosDesdeUltimaVersion;

  function refrescarPaso() {
    queryClient.invalidateQueries({ queryKey: ['rendicion', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-versiones', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-versiones-dropdown', rendicionId] });
    queryClient.invalidateQueries({ queryKey: ['rendicion-validaciones', rendicionId] });
  }

  // Genera el PDF borrador: el backend persiste fila Version con esBorrador=true
  // y devuelve el PDF binario para descarga inmediata.
  async function generarBorrador() {
    setDescargandoBorrador(true);
    try {
      const res = await rendicionAnualService.descargarPdfBorrador(rendicionId);
      // Descargar el blob
      const blob = new Blob([res.data as any], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rendicion-anual-${rendicion.ejercicioNumero}-borrador.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Borrador generado y descargado. Llevalo al CPCECh para certificar.');
      refrescarPaso();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? err?.message ?? 'No se pudo generar');
    } finally {
      setDescargandoBorrador(false);
    }
  }

  async function descargarPdfVersion(numeroVersion: number) {
    try {
      const res = await rendicionAnualService.descargarVersionPdf(rendicionId, numeroVersion);
      const url = res.data?.url;
      if (url) window.open(url, '_blank');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'No se pudo descargar');
    }
  }

  // Sube el PDF certificado por CPCECh como Documento y, en la misma operación,
  // cristaliza la versión actual (esBorrador → false). El "firmado" es físico (sello
  // y firma del contador + CPCECh sobre el papel); subirlo al sistema es la única
  // acción del partido y equivale a confirmar la versión como definitiva.
  async function subirPdfFirmado(file: File) {
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('archivo', file);
      formData.append('tipo', 'CERTIFICACION_CPCE');
      formData.append('partidoId', partidoId);
      formData.append('titulo', `Certificación CPCECh — Rendición ${rendicion.ejercicioNumero} v${rendicion.versionActual}`);
      const upRes = await documentoService.upload(formData);
      const docId = upRes.data.data?.id ?? upRes.data.id;
      if (!docId) throw new Error('No se pudo subir el documento');

      // Cristalizar versión: pasa de borrador → firmada
      await rendicionAnualService.generarVersion(rendicionId, { documentoId: docId });

      setDocFirmadoNombre(file.name);
      toast.success('PDF firmado guardado. La versión quedó firmada — ya podés enviar.');
      refrescarPaso();
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? err?.message ?? 'No se pudo subir');
    } finally {
      setUploadingDoc(false);
    }
  }

  const presentarMutation = useMutation({
    mutationFn: async () => {
      if (!ddjjAceptada) throw new Error('Debés aceptar la declaración jurada');
      return rendicionAnualService.presentar(rendicionId, { declaracionJurada: true });
    },
    onSuccess: (res) => {
      toast.success(`Rendición presentada. Expediente ${res.data.numeroExpediente}`);
      queryClient.invalidateQueries({ queryKey: ['rendicion', rendicionId] });
      queryClient.invalidateQueries({ queryKey: ['rendiciones-partido', partidoId] });
      onPresentada({ ...res.data, esRepresentacion });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? err?.message ?? 'No se pudo enviar'),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end -mb-2">
        <BotonRefresh onClick={refrescarPaso} title="Actualizar paso" />
      </div>

      {/* Validaciones contables */}
      {validacionesData && validacionesData.cantidadTotal > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between border-b border-gray-200 pb-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Validaciones contables</h2>
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                validacionesOk ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              {validacionesOk
                ? `✓ ${validacionesData.cantidadOk}/${validacionesData.cantidadTotal} OK`
                : `✗ ${validacionesData.cantidadOk}/${validacionesData.cantidadTotal} OK`}
            </span>
          </div>
          {!validacionesOk && (
            <div className="bg-red-50 border-l-4 border-red-400 p-3 text-sm text-red-900 mb-3">
              No se puede generar el borrador hasta que todas las validaciones estén en verde.
              Volvé a los pasos correspondientes y corregí lo señalado.
            </div>
          )}
          <ul className="space-y-2">
            {validacionesData.validaciones.map((v) => (
              <li key={v.codigo} className={`flex items-start gap-3 p-2 rounded ${v.ok ? 'bg-green-50' : 'bg-red-50'}`}>
                <span
                  className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                    v.ok ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                  }`}
                >
                  {v.ok ? '✓' : '✗'}
                </span>
                <div className="flex-1 text-sm">
                  <div className={v.ok ? 'text-gray-800' : 'text-red-900 font-medium'}>{v.titulo}</div>
                  {v.detalle && (
                    <div className={`text-xs ${v.ok ? 'text-gray-500' : 'text-red-700'} font-mono`}>
                      {v.detalle}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Bloque 1: borrador → certificación → firmar versión */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
          1. Versión firmada
        </h2>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-3 text-sm text-blue-900 mb-4 space-y-1">
          <p><strong>Flujo:</strong></p>
          <ol className="list-decimal ml-5 space-y-0.5">
            <li>Generá el <strong>PDF borrador</strong>: queda guardado como versión y se descarga.</li>
            <li>Firmalo (Contador + Presidente + Tesorero) y envialo al CPCECh para certificación.</li>
            <li>Subí acá el PDF firmado: la versión queda firmada automáticamente.</li>
            <li>Si modificás datos antes de firmar, el borrador se descarta automáticamente.</li>
          </ol>
        </div>

        {/* Banner: la versión está desactualizada respecto a los datos actuales */}
        {versionDesactualizada && (
          <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 rounded-r p-3 text-sm text-amber-900 flex items-start gap-2">
            <span className="text-lg leading-none">⚠</span>
            <div>
              <strong>Hay cambios desde la última versión generada.</strong>
              <span className="block text-xs text-amber-800 mt-0.5">
                Modificaste datos del wizard después de la versión N° {rendicion.versionActual}. La única
                acción permitida es <strong>generar un nuevo PDF borrador</strong> con los datos
                actualizados. El upload del PDF firmado y el envío están bloqueados hasta entonces.
              </span>
            </div>
          </div>
        )}

        {/* Estado de la versión actual */}
        {tieneVersion ? (
          <div className={`mb-4 p-3 rounded border ${versionEsBorrador ? 'bg-amber-50 border-amber-300' : 'bg-green-50 border-green-300'}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm">
                <span className="font-semibold">Versión N° {rendicion.versionActual}</span>
                <span
                  className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    versionEsBorrador ? 'bg-amber-200 text-amber-900' : 'bg-green-200 text-green-900'
                  }`}
                >
                  {versionEsBorrador ? 'borrador' : 'firmada'}
                </span>
                <div className="text-xs font-mono text-gray-600 mt-1 break-all">
                  Hash: {rendicion.hashActual}
                </div>
              </div>
              <button
                type="button"
                onClick={() => descargarPdfVersion(rendicion.versionActual)}
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium px-3 py-1.5 rounded"
              >
                Descargar PDF
              </button>
            </div>
            {versionEsBorrador && (
              <p className="text-xs text-amber-800 mt-2">
                El PDF borrador descargado lleva impreso el hash. Llevalo a firmar y certificar; cuando
                vuelva con sello CPCECh subilo abajo.
              </p>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic mb-4">Todavía no se generó ninguna versión.</p>
        )}

        {!readonly && (
          <div className="flex flex-wrap items-center gap-3">
            {/* Botón generar borrador */}
            <button
              type="button"
              onClick={generarBorrador}
              disabled={!validacionesOk || descargandoBorrador}
              title={
                !validacionesOk
                  ? 'No se puede generar: las validaciones no están OK'
                  : versionDesactualizada
                  ? 'Hay cambios desde la última versión — regenerá el PDF borrador con los datos actuales'
                  : versionEsBorrador
                  ? 'Volver a descargar el PDF borrador (regenera con datos actuales)'
                  : versionEsFirmada
                  ? 'Generar un nuevo borrador (descarta la versión firmada actual y crea una nueva versión)'
                  : 'Generar el PDF borrador'
              }
              className={`text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50 ${
                versionDesactualizada || versionEsFirmada
                  ? 'bg-amber-600 hover:bg-amber-700'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {descargandoBorrador
                ? 'Generando...'
                : versionDesactualizada
                ? '🔄 Regenerar borrador (datos cambiaron)'
                : versionEsBorrador
                ? '🔄 Regenerar PDF borrador'
                : versionEsFirmada
                ? '🔄 Nuevo borrador (modifica versión)'
                : '📄 Generar PDF borrador'}
            </button>
          </div>
        )}

        {/* Subir PDF firmado para cristalizar */}
        {!readonly && (
          <div className="mt-5 pt-5 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Subir PDF firmado y certificado</h3>
            <p className="text-xs text-gray-600 mb-2">
              Cuando vuelva del CPCECh con sello y firma, subí acá el PDF certificado para asociarlo a la
              versión actual.
            </p>

            {!tieneVersion && (
              <div className="mb-2 bg-gray-50 border border-gray-200 rounded p-2 text-xs text-gray-600">
                Generá primero el PDF borrador (botón de arriba). Después podrás subir el firmado.
              </div>
            )}
            {versionEsFirmada && !versionDesactualizada && (
              <div className="mb-2 bg-blue-50 border border-blue-200 rounded p-2 text-xs text-blue-900">
                La versión actual ya está firmada. Si querés reemplazar el PDF certificado, primero generá
                un nuevo borrador (eso crea una nueva versión a firmar).
              </div>
            )}
            {versionDesactualizada && (
              <div className="mb-2 bg-amber-50 border border-amber-300 rounded p-2 text-xs text-amber-900">
                Hay cambios sin reflejar en la versión. Regenerá el PDF borrador antes de subir el firmado.
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              disabled={uploadingDoc || !versionEsBorrador || versionDesactualizada}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) subirPdfFirmado(f);
              }}
              className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-amber-50 file:text-amber-700 file:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {uploadingDoc && <p className="mt-1 text-xs text-gray-600">Subiendo y firmando versión...</p>}
            {!uploadingDoc && docFirmadoNombre && versionEsFirmada && (
              <div className="mt-2 bg-green-50 border border-green-300 rounded px-3 py-2 text-sm text-green-800">
                ✓ PDF firmado guardado: <strong>{docFirmadoNombre}</strong>
              </div>
            )}
          </div>
        )}

        {/* Historial de versiones */}
        {versiones.length > 0 && (
          <div className="mt-5 pt-5 border-t border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Historial de versiones</h3>
            <div className="overflow-x-auto border border-gray-200 rounded">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">N°</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hash</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {versiones.map((v) => (
                    <tr key={v.id}>
                      <td className="px-3 py-2 font-semibold">
                        {v.numeroVersion === rendicion.versionActual ? (
                          <span className="text-green-700">N° {v.numeroVersion} ●</span>
                        ) : (
                          <span className="text-gray-500">N° {v.numeroVersion}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            v.esBorrador ? 'bg-amber-200 text-amber-900' : 'bg-green-200 text-green-900'
                          }`}
                        >
                          {v.esBorrador ? 'borrador' : 'firmada'}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-gray-700 break-all max-w-[200px]">
                        {v.hashCode.slice(0, 16)}...
                      </td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{formatFecha(v.fechaGeneracion)}</td>
                      <td className="px-3 py-2 text-gray-700 text-xs">
                        {v.usuario ? `${v.usuario.nombre} ${v.usuario.apellido}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => descargarPdfVersion(v.numeroVersion)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          Descargar PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Bloque 2: DDJJ + envío */}
      {!readonly && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-3 mb-4">
            2. Declaración Jurada y Envío
          </h2>

          {esRepresentacion && (
            <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 rounded-r p-3 text-xs text-amber-900">
              Re-presentación tras observación — al enviar el expediente vinculado volverá al estado{' '}
              <strong>En análisis</strong>.
            </div>
          )}

          <label className="flex items-start gap-2 bg-gray-50 border border-gray-300 rounded p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={ddjjAceptada}
              onChange={(e) => setDdjjAceptada(e.target.checked)}
              className="mt-0.5"
              disabled={!versionEsFirmada || versionDesactualizada}
            />
            <span className="text-sm text-gray-800">
              Declaro bajo juramento que los datos consignados en esta rendición son correctos y completos,
              reflejan fielmente la situación económica y patrimonial de la agrupación política, y fueron
              confeccionados según normas contables vigentes. <em>(Art. 53, Ley XII-23)</em>
            </span>
          </label>

          <div className="mt-4 flex items-center justify-between flex-wrap gap-3 border-t border-gray-200 pt-4">
            <div className="text-xs text-gray-600">
              Una vez enviada no podrá modificarse. La SEP tiene 30 días para aprobar o desaprobar{' '}
              <em>(Art. 54 Ley XII-23)</em>.
            </div>
            <button
              type="button"
              onClick={() => presentarMutation.mutate()}
              disabled={
                !validacionesOk ||
                !versionEsFirmada ||
                versionDesactualizada ||
                !ddjjAceptada ||
                presentarMutation.isPending
              }
              title={
                !validacionesOk
                  ? 'Las validaciones contables no están todas OK'
                  : !tieneVersion
                  ? 'Falta generar el borrador y firmarlo'
                  : versionDesactualizada
                  ? 'Hay cambios sin reflejar — regenerá el borrador'
                  : versionEsBorrador
                  ? 'La versión está en borrador. Subí el PDF firmado.'
                  : !ddjjAceptada
                  ? 'Falta aceptar la declaración jurada'
                  : 'Enviar rendición'
              }
              className="bg-green-600 hover:bg-green-700 text-white text-sm font-bold px-5 py-2.5 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {presentarMutation.isPending
                ? 'Enviando...'
                : esRepresentacion
                ? '✉️ Re-enviar Rendición Anual'
                : '✉️ Enviar Rendición Anual'}
            </button>
          </div>
        </div>
      )}

      {readonly && (
        <div className="bg-green-50 border-l-4 border-green-400 rounded-lg p-4 text-sm text-green-900">
          Esta rendición ya fue presentada (estado: <strong>{rendicion.estado}</strong>). No se puede modificar.
        </div>
      )}

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-5 py-2 rounded-lg"
        >
          ← Anterior
        </button>
      </div>
    </div>
  );
}
